/*
 * $Id:: index.js 2020/11/12 06:07 otsolka
 *
 *        . * .
 *      * RRRR  *   Copyright (c) 2012-2021 EUIPO: European Intelectual
 *     .  RR  R  .  Property Organization (trademarks and designs).
 *     *  RRR    *
 *      . RR RR .   ALL RIGHTS RESERVED
 *       *. _ .*
 *
 *  The use and distribution of this software is under the restrictions exposed in 'license.txt'
 */

import React, { useEffect, useState } from 'react';

import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import { useDispatch, useSelector } from 'react-redux';

import { globals as globalsSlice } from '../../../../app/globalsSlice';
import { ReactComponent as DropDownArrow } from '../../../../assets/images/unfold_more_24px.svg';
import { ReactComponent as LaunchIcon } from '../../../../assets/images/launch_24px.svg';
import Card from '../../../../components/Card';
import { t, getLocale, setLocale } from '../../../../providers';
import { me as meSlice } from '../../../AuthenticatedPage/authenticationSlice';
import helpers from '../../../SSOPage/SSOHelpers';
import { loading as loadingSlice, requestResult, deactivateResult } from '../../accountSlice';
import { SLICE_NAME } from '../../constants';
import CannotDeactivateDialog from './CannotDeactivateDialog';
import ConfirmDeactivateDialog from './ConfirmDeactivateDialog';
import DeactivateSuccessDialog from './DeactivateSuccessDialog';
import Grid from '@material-ui/core/Grid';

const useStyles = makeStyles((theme) => ({
    headerSection: {
        paddingBottom: '15px',
        paddingTop: '15px',
    },
    cardTitle: {
        fontSize: '1rem',
        fontWeight: '400',
        color: '#4D4D4D',
        marginTop: '10px',
        letterSpacing: '0.15px',
        fontStyle: 'normal',
        lineHeight: '125%',
    },
    addButton: {
        color: theme.palette.text.mainBlue,
        borderColor: theme.palette.text.mainBlue,
        borderRadius: '3px',
        height: '32px',
    },
    content: {
        marginTop: '3rem',
        marginBottom: '3rem',
    },
    childAccountBtn: {
        height: '32px',
        minWidth: '10rem',
        borderRadius: '2px',
        marginLeft: '0',
        '&:focus-visible': {
            backgroundColor: 'rgba(209, 224, 194, 0.2)',
            outline: 'none',
            border: '2px dashed #4F7E1D',
            borderRadius: '3px',
        },
    },
}));

const Preferences = () => {
    const classes = useStyles();

    const lang = getLocale();

    const dispatch = useDispatch();
    const me = useSelector(meSlice);
    const response = useSelector(requestResult);
    const deactivateResponse = useSelector(deactivateResult);
    const loading = useSelector(loadingSlice);
    const { globals } = useSelector(globalsSlice);
    const [language, setLanguage] = useState(lang);
    const [wantsNotifications, setWantsNotification] = useState(false);
    const [cannotDeactivateOpen, setCannotDeactivateOpen] = useState(false);
    const [confirmDeactivateOpen, setConfirmDeactivateOpen] = useState(false);
    const [deactivateSuccessOpen, setDeactivateSuccessOpen] = useState(false);

    useEffect(() => {
        const { username } = me;
        if (username && (!me || !me.email)) dispatch({ type: `${SLICE_NAME}/getAccountDetails`, payload: { userName: username } });
    }, [dispatch, me]);

    useEffect(() => {
        if (response && response.language !== language) {
            setLanguage(response.language);
            let myStorage = window.sessionStorage;
            myStorage.setItem('lang', language);
            dispatch({ type: `${SLICE_NAME}/requestResult`, payload: null });
        }

        return () => {
            dispatch({ type: `${SLICE_NAME}/requestResult`, payload: null });
        };
    }, [response, dispatch, language]);

    useEffect(() => {
        const { action, reason, valid } = deactivateResponse || {};
        if (!!deactivateResponse) {
            if (!valid && reason === 'ACTIVE_CHILD_ACCOUNTS') {
                setCannotDeactivateOpen(true);
            }
            if (valid && action === 'deactivate') {
                setConfirmDeactivateOpen(true);
            }
            if (valid && action === 'confirmDeactivate') {
                setDeactivateSuccessOpen(true);
            }
            dispatch({ type: `${SLICE_NAME}/deactivateResult`, payload: null });
        }
    }, [deactivateResponse, dispatch]);

    const closeCannotDeactivateDialog = (event) => {
        event.preventDefault();
        setCannotDeactivateOpen(false);
    };

    const closeConfirmation = (event) => {
        event.preventDefault();
        setConfirmDeactivateOpen(false);
    };

    const deactivateMyAccount = (event) => {
        event.preventDefault();
        dispatch({ type: `${SLICE_NAME}/confirmDeactivateParentAccount`, payload: null });
        setConfirmDeactivateOpen(false);
    };

    const logout = (_event) => {
        helpers.logout();
    };

    const onSuccessDeactivate = (event) => {
        event.preventDefault();
        logout();
    };

    const updateLanguage = (event) => {
        const value = event.target.value;
        if (language === value) return;
        dispatch({ type: `${SLICE_NAME}/updateLanguage`, payload: { language: value } });
    };

    const manageAccount = (event) => {
    	const manageAccountLink = `${globals.accountLinks['inpi.account.text']['link']}${lang}`;
        window.open(manageAccountLink, '_blank');
    };

    const editAccount = (event) => {
        event.preventDefault();
        const lang = getLocale();
        window.open(`${globals.accountLinks['inpi.account.text']['link']}${lang}`, '_blank');
    };

    const languages = Object.keys(globals.supportedLanguages || {}).map((key) => globals.supportedLanguages[key]);
    const showNotificationsCheckbox = false;
    const render = (
        <div style={{ marginTop: '-48px' }}>
            <Card canEdit={false}>
                <Grid
                    justify={'space-between'}
                    alignItems={'center'}
                    container
                    className={classes.headerSection}
                    style={{ borderBottom: '1px solid #ccc' }}>
                    <Grid item>
                        <Typography className={classes.cardTitle}>{t('preferred.language')}</Typography>
                    </Grid>
                    <Grid item>
                    <Select
                        className={classes.textInput}
                        displayEmpty
                        label={t('select.language')}
                        onChange={updateLanguage}
                        value={language}
                        IconComponent={DropDownArrow}
                        inputProps={{ 'aria-haspopup': 'listbox' }}>
                        <MenuItem value="" disabled className={classes.disabledMenuItem} role="option">
                            {t('select.language')}
                        </MenuItem>
                        {languages.map(({ key, value, code }, idx) => (
                            <MenuItem key={`${key}_${idx}`} value={code}>
                                {value}
                            </MenuItem>
                        ))}
                        </Select>
                    </Grid>
                </Grid>
                <Grid
                    justify={'space-between'}
                    alignItems={'center'}
                    container
                    className={classes.headerSection}
                    style={{ borderBottom: '1px solid #ccc' }}>
                    <Grid item>
                        <Typography className={classes.cardTitle}>{t('account.details')}</Typography>
                    </Grid>
                    <Grid item>
                        <Button
                            disableFocusRipple
                            aria-label={`${t('edit.my.account')}. ${t('open.new.tab')}`}
                            className={classes.childAccountBtn}
                            variant={'outlined'}
                            onClick={editAccount}>
                            {t('edit.my.account').toUpperCase()}
                            <LaunchIcon style={{ paddingLeft: '5px' }} aria-hidden="true" focusable="false" />
                        </Button>
                    </Grid>
                </Grid>
            </Card>
            <CannotDeactivateDialog open={cannotDeactivateOpen} onAccept={closeCannotDeactivateDialog} />
            <ConfirmDeactivateDialog open={confirmDeactivateOpen} onAccept={deactivateMyAccount} onCancel={closeConfirmation} />
            <DeactivateSuccessDialog open={deactivateSuccessOpen} onAccept={onSuccessDeactivate} />
        </div>
    );

    return loading ? <CircularProgress /> : render;
};

export default Preferences;

/*
    
*/